/**
 * crew.js — the app-wide crew index: who is flying what, right now.
 *
 * RASCI was retired 2026-09-02 (docs/FLOW_RECONCILIATION_PLAN.md). Crews are the only assignment
 * mechanism, they are made only on the Flight Board, and a queued milestone has NO owner by design.
 * Every surface that used to read RASCI to answer "who's on this" reads this module instead:
 * the outline's crew chips (CrewInline.jsx), the milestone/thread zoom titles, My Work, /mine.
 *
 * One fetch (services/flights.js#getCrewIndex — three Supabase reads, no Airtable), cached on
 * `ctx.crewIndex`, rebuilt whenever a crew write announces itself (`crew-index-changed`, fired by
 * the flights service after the server confirmed the change). Readers follow the outline's usual
 * lazy sentinel contract:
 *
 *   undefined  not started (the first read kicks it off)
 *   null       in flight
 *   object     loaded — { flights, byMilestone, byThread, byMember, at }
 *
 * `crewForMilestone` / `crewForThread` return `undefined` while loading, `null` when the entity
 * has no open flight (queued, or landed), and the flight object when it is in the air.
 */

import { ctx } from './ctx.js'
import { getCrewIndex } from '../services/flights.js'

function build(flights, landed) {
  const byMilestone = {}, byThread = {}, byMember = {}, landedByMember = {}
  for (const f of flights) {
    if (f.milestoneId) byMilestone[f.milestoneId] = f
    if (f.threadId) byThread[f.threadId] = f
    // A squadron's member threads resolve to the squadron — the crew on the squadron IS the crew
    // on each of its threads.
    for (const t of (f.threads || [])) if (t.threadId && !byThread[t.threadId]) byThread[t.threadId] = f
    for (const c of (f.crew || [])) {
      if (!byMember[c.memberAirtableId]) byMember[c.memberAirtableId] = []
      byMember[c.memberAirtableId].push(f)
    }
  }
  for (const l of (landed || [])) {
    for (const mid of (l.memberIds || [])) {
      if (!landedByMember[mid]) landedByMember[mid] = []
      landedByMember[mid].push(l)
    }
  }
  return { flights, landed: landed || [], byMilestone, byThread, byMember, landedByMember, at: Date.now() }
}

let inflight = null
let failures = 0
const RETRY_MS = [2000, 4000, 8000, 15000]   // then give up until something calls refreshCrewIndex()

/** Kick off (or return) the index load. Resolves to the built index; never throws to callers.
 *
 * A failed load is NOT the same as "nobody is crewed": the empty index it leaves behind is flagged
 * `failed` and retried on a short backoff, because the classic failure is a request that raced the
 * sign-in token at page load (see services/flights.js#authToken) — recoverable seconds later. The
 * UI reads crewIndexFailed() to say "couldn't load" instead of "not crewed" in the meantime. */
export function ensureCrewIndex() {
  if (ctx.crewIndex && ctx.crewIndex.flights) return Promise.resolve(ctx.crewIndex)
  if (inflight) return inflight
  ctx.crewIndex = null
  inflight = getCrewIndex()
    .then(d => { ctx.crewIndex = build(d.flights || [], d.landed || []); failures = 0 })
    .catch(() => {
      ctx.crewIndex = build([], [])
      ctx.crewIndex.failed = true
      const wait = RETRY_MS[Math.min(failures, RETRY_MS.length - 1)]
      failures += 1
      if (typeof window !== 'undefined' && failures <= RETRY_MS.length) {
        setTimeout(() => { if (ctx.crewIndex && ctx.crewIndex.failed) refreshCrewIndex() }, wait)
      }
    })
    .then(() => {
      inflight = null
      if (typeof window !== 'undefined') window.dispatchEvent(new Event('outline-render'))
      return ctx.crewIndex
    })
  return inflight
}

/** True when the last load failed (the index is empty because of an error, not because no one flies). */
export function crewIndexFailed() { return !!(ctx.crewIndex && ctx.crewIndex.failed) }

/** Drop the cache and reload. Called on `crew-index-changed`; safe to call any time. */
export function refreshCrewIndex() {
  ctx.crewIndex = undefined
  return ensureCrewIndex()
}

function lookup(map, id) {
  if (!id) return null
  const idx = ctx.crewIndex
  if (idx === undefined) { ensureCrewIndex(); return undefined }
  if (idx === null) return undefined
  return idx[map][id] || null
}

/** The open flight a milestone is on, or null when it is queued / landed. undefined while loading. */
export function crewForMilestone(msId) { return lookup('byMilestone', msId) }

/** The open flight (thread crew or squadron) a thread is on, or null. undefined while loading. */
export function crewForThread(threadId) { return lookup('byThread', threadId) }

/** Every open flight a person is crewed on (at most two by database rule). [] until loaded. */
export function flightsForMember(memberId) {
  if (!memberId) return []
  const idx = ctx.crewIndex
  if (idx === undefined) { ensureCrewIndex(); return [] }
  if (idx === null) return []
  return idx.byMember[memberId] || []
}

/** Milestone flights this person helped land in the last ~90 days (accomplishments). [] until loaded. */
export function landedForMember(memberId) {
  if (!memberId) return []
  const idx = ctx.crewIndex
  if (idx === undefined) { ensureCrewIndex(); return [] }
  if (idx === null) return []
  return idx.landedByMember[memberId] || []
}

/** True once the index has resolved (success or failure) — lets a row tell "queued" from "still loading". */
export function crewIndexReady() { return !!(ctx.crewIndex && ctx.crewIndex.flights) }

/** "Sara Z." — the short form the outline uses for people. */
export function shortName(name) {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean)
  if (!parts.length) return ''
  if (parts.length === 1) return parts[0]
  return parts[0] + ' ' + parts[parts.length - 1][0] + '.'
}

/** "CFW101 · Sara Z. · Emilio M." — captain first (the server already sorts the crew that way). */
export function crewLabel(flight) {
  if (!flight) return ''
  const names = (flight.crew || []).map(c => shortName(c.name)).filter(Boolean)
  return [flight.flightNumber, ...names].filter(Boolean).join(' · ')
}

if (typeof window !== 'undefined') {
  window.addEventListener('crew-index-changed', () => { refreshCrewIndex() })
}
